import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import i18n from "@/i18n";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import type {
  QuestionRoutingRule,
  QuestionVisibilityRule,
  QuestionVisibilityRuleExpr,
} from "@digital-twins/shared-types";
import {
  getVisibilityRuleLeaves,
  isVisibilityRuleGroup,
} from "@digital-twins/shared-types";
import { ChevronDown, ChevronRight, GitBranch } from "lucide-react";

import type {
  SkipLogicDetection,
  SkipLogicDetectionRoutingRule,
  SkipLogicDetectionRule,
  SkipLogicNotAdded,
} from "@digital-twins/shared-types";
import { formatIssueCopy } from "../utils/questionnaireIssueCopy";

const OPERATOR_LABELS: Record<
  Exclude<QuestionVisibilityRule["operator"], "is_not_applicable">,
  string
> = {
  equals: "is",
  not_equals: "is not",
  any_of: "is any of",
  none_of: "is none of",
  eq: "is equal to",
  gt: "is greater than",
  gte: "is at least",
  lt: "is less than",
  lte: "is at most",
  between: "is between",
  rank_equals: "is ranked at",
  rank_in_top: "is in top",
  rank_in_bottom: "is in bottom",
};

function describeVisibilityRuleLeaf(
  rule: QuestionVisibilityRule,
  questionTitleByStableKey: Map<string, string>,
  optionLabelByStableKey: Map<string, Map<string, string>>,
): string {
  const dependencyTitle =
    questionTitleByStableKey.get(rule.dependsOnStableKey) ??
    rule.dependsOnStableKey;
  if (
    rule.operator === "eq" ||
    rule.operator === "gt" ||
    rule.operator === "gte" ||
    rule.operator === "lt" ||
    rule.operator === "lte" ||
    rule.operator === "between" ||
    rule.operator === "is_not_applicable"
  ) {
    const cellLabel =
      typeof rule.matrixRowValue === "string"
        ? [
            rule.matrixRowValue,
            typeof rule.matrixMeasureId === "string"
              ? rule.matrixMeasureId
              : null,
          ]
            .filter(Boolean)
            .join(" / ")
        : null;
    const subject = cellLabel
      ? `${dependencyTitle} (${cellLabel})`
      : dependencyTitle;
    if (rule.operator === "is_not_applicable") {
      return `${subject} ${i18n.t(
        "questionnaire.logic.editor.operatorIsNotApplicable",
        { ns: "app" },
      )}`;
    }
    if (rule.operator === "between") {
      return `${subject} ${OPERATOR_LABELS.between} ${rule.threshold ?? "?"} and ${rule.thresholdHigh ?? "?"}`;
    }
    return `${subject} ${OPERATOR_LABELS[rule.operator]} ${rule.threshold ?? "?"}`;
  }
  const optionLabels = optionLabelByStableKey.get(rule.dependsOnStableKey);
  const valueLabels = (rule.values ?? [])
    .map((value) => optionLabels?.get(value) ?? value)
    .join(", ");
  if (
    rule.operator === "rank_equals" ||
    rule.operator === "rank_in_top" ||
    rule.operator === "rank_in_bottom"
  ) {
    return `${dependencyTitle}: ${valueLabels} ${OPERATOR_LABELS[rule.operator]} ${rule.rankK ?? "?"}`;
  }
  // A choice grid's condition reads one row's answer.
  const subject =
    typeof rule.matrixRowValue === "string"
      ? `${dependencyTitle} (${rule.matrixRowValue})`
      : dependencyTitle;
  return `${subject} ${OPERATOR_LABELS[rule.operator]} ${valueLabels}`;
}

function describeVisibilityRule(
  rule: QuestionVisibilityRuleExpr,
  questionTitleByStableKey: Map<string, string>,
  optionLabelByStableKey: Map<string, Map<string, string>>,
): string {
  if (isVisibilityRuleGroup(rule)) {
    const joinLabel = rule.join === "or" ? " or " : " and ";
    return getVisibilityRuleLeaves(rule)
      .map((leaf) =>
        describeVisibilityRuleLeaf(
          leaf,
          questionTitleByStableKey,
          optionLabelByStableKey,
        ),
      )
      .join(joinLabel);
  }
  return describeVisibilityRuleLeaf(
    rule,
    questionTitleByStableKey,
    optionLabelByStableKey,
  );
}

function describeRoutingRule(
  rule: QuestionRoutingRule,
  questionTitleByStableKey: Map<string, string>,
  optionLabelByStableKey: Map<string, Map<string, string>>,
): string {
  const whenLabel = describeVisibilityRule(
    rule.when,
    questionTitleByStableKey,
    optionLabelByStableKey,
  );
  if (rule.action === "end_survey") {
    return `when ${whenLabel} → end survey`;
  }
  const targetTitle =
    questionTitleByStableKey.get(rule.targetStableKey ?? "") ??
    rule.targetStableKey ??
    "?";
  return `when ${whenLabel} → skip to ${targetTitle}`;
}

type ProposalSource = Exclude<SkipLogicDetectionRule["source"], "preserved">;

const SOURCE_BADGE_CLASSES: Record<ProposalSource, string> = {
  explicit:
    "border-emerald-300/60 bg-emerald-50 text-emerald-800 dark:border-emerald-400/30 dark:bg-emerald-400/10 dark:text-emerald-300",
  implied:
    "border-sky-300/60 bg-sky-50 text-sky-800 dark:border-sky-400/30 dark:bg-sky-400/10 dark:text-sky-300",
};

/**
 * One rule. A proposal carries its checkbox, source, confidence and the
 * model's one-line reason; a rule the question already has is shown as it
 * stands, with nothing about how the detector treated it; a rule not added
 * carries the line saying why.
 */
function RuleRow(props: {
  id: string;
  targetTitle: string;
  description: string;
  effectLabel: string;
  note?: string;
  proposal?: {
    source: SkipLogicDetectionRule["source"];
    confidence: number;
    rationale: string;
    checked: boolean;
    onToggle: () => void;
  };
}) {
  const { id, targetTitle, description, effectLabel, note, proposal } = props;
  const { t } = useTranslation("app");
  const source =
    proposal && proposal.source !== "preserved" ? proposal.source : null;
  return (
    <div
      className="flex items-start gap-3 rounded-[8px] border border-border bg-background px-3 py-2.5 dark:bg-card"
      data-testid={`skip-logic-rule-row-${id}`}
    >
      {proposal ? (
        <Checkbox
          checked={proposal.checked}
          onCheckedChange={() => proposal.onToggle()}
          className="mt-0.5"
          aria-label={t("questionnaire.skipReview.applyAria", {
            title: targetTitle,
            description,
          })}
          data-testid={`skip-logic-rule-checkbox-${id}`}
        />
      ) : (
        <GitBranch className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
      )}
      <div className="flex-1 space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[13px] font-medium text-foreground">
            {targetTitle}
          </span>
          {source ? (
            <span
              className={`rounded-full border px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide ${SOURCE_BADGE_CLASSES[source]}`}
            >
              {t(`questionnaire.skipReview.source.${source}`)}
            </span>
          ) : null}
          {proposal ? (
            <span className="text-[11px] text-muted-foreground">
              {t("questionnaire.skipReview.confidence", {
                percent: Math.round(proposal.confidence * 100),
              })}
            </span>
          ) : null}
        </div>
        <p className="text-[12.5px] text-muted-foreground">
          {effectLabel}{" "}
          <span className="font-medium text-foreground">{description}</span>
        </p>
        {proposal?.rationale ? (
          <p className="text-[11.5px] italic text-muted-foreground/80">
            {proposal.rationale}
          </p>
        ) : null}
        {note ? (
          <p className="text-[11.5px] text-muted-foreground/80">{note}</p>
        ) : null}
      </div>
    </div>
  );
}

function CollapsibleReportSection(props: {
  title: string;
  count: number;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  if (props.count === 0) {
    return null;
  }
  return (
    <div className="rounded-[8px] border border-dashed border-border">
      <button
        type="button"
        className="flex w-full items-center gap-1.5 px-3 py-2 text-left text-[12px] font-medium text-muted-foreground hover:text-foreground"
        onClick={() => setOpen((prev) => !prev)}
        data-testid={`skip-logic-report-toggle-${props.title.toLowerCase().replace(/\s+/g, "-")}`}
      >
        {open ? (
          <ChevronDown className="h-3.5 w-3.5" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5" />
        )}
        {props.title} ({props.count})
      </button>
      {open ? (
        <div className="flex flex-col gap-1.5 border-t border-dashed border-border px-3 py-2">
          {props.children}
        </div>
      ) : null}
    </div>
  );
}

export interface SkipLogicApplySelection {
  visibilityRules: Array<SkipLogicDetectionRule>;
  routingRules: Array<SkipLogicDetectionRoutingRule>;
}

export interface SkipLogicReviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  result: SkipLogicDetection | null;
  questionTitleByStableKey: Map<string, string>;
  optionLabelByStableKey: Map<string, Map<string, string>>;
  onApply: (approved: SkipLogicApplySelection) => void;
  isApplying?: boolean;
}
// `questionTitleByStableKey` names each question as the builder does, "Q6 ·
// title" (`questionNumberedTitle`), so two questions that share a title can be
// told apart.

/**
 * Review modal for the "Detect skip logic" flow. Proposals (explicit/implied)
 * default checked and can be individually rejected; the rules questions already
 * have are listed, collapsed, as they stand; so are the proposals the Save's
 * checks refused, each with the line saying why. What the detector considered
 * and left out is not shown.
 */
export function SkipLogicReviewDialog(props: SkipLogicReviewDialogProps) {
  const {
    open,
    onOpenChange,
    result,
    questionTitleByStableKey,
    optionLabelByStableKey,
    onApply,
    isApplying,
  } = props;
  const { t } = useTranslation("app");

  const [approvedVisibility, setApprovedVisibility] = useState<
    Record<string, boolean>
  >({});
  const [approvedRouting, setApprovedRouting] = useState<
    Record<string, boolean>
  >({});

  useEffect(() => {
    if (!open || !result) {
      return;
    }
    const visibilitySeeded: Record<string, boolean> = {};
    result.rules
      .filter((rule) => rule.source !== "preserved")
      .forEach((rule) => {
        visibilitySeeded[rule.targetStableKey] = true;
      });
    setApprovedVisibility(visibilitySeeded);

    const routingSeeded: Record<string, boolean> = {};
    (result.routingRules ?? [])
      .filter((rule) => rule.source !== "preserved")
      .forEach((rule) => {
        routingSeeded[rule.sourceStableKey] = true;
      });
    setApprovedRouting(routingSeeded);
  }, [open, result]);

  if (!result) {
    return null;
  }

  const routingRules = result.routingRules ?? [];
  const actionableVisibility = result.rules.filter(
    (rule) => rule.source !== "preserved",
  );
  const preservedVisibility = result.rules.filter(
    (rule) => rule.source === "preserved",
  );
  const actionableRouting = routingRules.filter(
    (rule) => rule.source !== "preserved",
  );
  const preservedRouting = routingRules.filter(
    (rule) => rule.source === "preserved",
  );
  const approvedCount =
    actionableVisibility.filter((rule) => approvedVisibility[rule.targetStableKey])
      .length +
    actionableRouting.filter((rule) => approvedRouting[rule.sourceStableKey])
      .length;
  const actionableTotal =
    actionableVisibility.length + actionableRouting.length;
  const keptTotal = preservedVisibility.length + preservedRouting.length;

  const notAdded = result.notAdded ?? [];

  const titleFor = (stableKey: string) =>
    questionTitleByStableKey.get(stableKey) ?? stableKey;
  const describeNotAdded = (item: SkipLogicNotAdded) =>
    item.visibilityRule
      ? describeVisibilityRule(
          item.visibilityRule,
          questionTitleByStableKey,
          optionLabelByStableKey,
        )
      : item.routingRule
        ? describeRoutingRule(
            item.routingRule,
            questionTitleByStableKey,
            optionLabelByStableKey,
          )
        : "";

  const handleApply = () => {
    onApply({
      visibilityRules: actionableVisibility.filter(
        (rule) => approvedVisibility[rule.targetStableKey],
      ),
      routingRules: actionableRouting.filter(
        (rule) => approvedRouting[rule.sourceStableKey],
      ),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg"
        data-testid="skip-logic-review-dialog"
      >
        <DialogHeader>
          <DialogTitle>
            {actionableTotal > 0
              ? t("questionnaire.skipReview.title")
              : t("questionnaire.skipReview.titleNone")}
          </DialogTitle>
          <DialogDescription>
            {actionableTotal > 0
              ? t("questionnaire.skipReview.review", { count: actionableTotal })
              : keptTotal > 0
                ? t("questionnaire.skipReview.noneKept")
                : t("questionnaire.skipReview.none")}
          </DialogDescription>
        </DialogHeader>

        <div className="flex max-h-[50vh] flex-col gap-2 overflow-y-auto py-1">
          {actionableVisibility.map((rule) => (
            <RuleRow
              key={`vis-${rule.targetStableKey}`}
              id={rule.targetStableKey}
              targetTitle={titleFor(rule.targetStableKey)}
              description={describeVisibilityRule(
                rule.visibilityRule,
                questionTitleByStableKey,
                optionLabelByStableKey,
              )}
              effectLabel={t("questionnaire.skipReview.shownOnlyIf")}
              proposal={{
                source: rule.source,
                confidence: rule.confidence,
                rationale: rule.rationale,
                checked: Boolean(approvedVisibility[rule.targetStableKey]),
                onToggle: () =>
                  setApprovedVisibility((prev) => ({
                    ...prev,
                    [rule.targetStableKey]: !prev[rule.targetStableKey],
                  })),
              }}
            />
          ))}

          {actionableRouting.map((rule) => (
            <RuleRow
              key={`route-${rule.sourceStableKey}`}
              id={`routing-${rule.sourceStableKey}`}
              targetTitle={titleFor(rule.sourceStableKey)}
              description={describeRoutingRule(
                rule.routingRule,
                questionTitleByStableKey,
                optionLabelByStableKey,
              )}
              effectLabel={t("questionnaire.skipReview.routing")}
              proposal={{
                source: rule.source,
                confidence: rule.confidence,
                rationale: rule.rationale,
                checked: Boolean(approvedRouting[rule.sourceStableKey]),
                onToggle: () =>
                  setApprovedRouting((prev) => ({
                    ...prev,
                    [rule.sourceStableKey]: !prev[rule.sourceStableKey],
                  })),
              }}
            />
          ))}

          {keptTotal > 0 ? (
            <CollapsibleReportSection
              title={t("questionnaire.skipReview.alreadyApplied")}
              count={keptTotal}
            >
              {preservedVisibility.map((rule) => (
                <RuleRow
                  key={`vis-pres-${rule.targetStableKey}`}
                  id={`preserved-${rule.targetStableKey}`}
                  targetTitle={titleFor(rule.targetStableKey)}
                  description={describeVisibilityRule(
                    rule.visibilityRule,
                    questionTitleByStableKey,
                    optionLabelByStableKey,
                  )}
                  effectLabel={t("questionnaire.skipReview.shownOnlyIf")}
                />
              ))}
              {preservedRouting.map((rule) => (
                <RuleRow
                  key={`route-pres-${rule.sourceStableKey}`}
                  id={`preserved-routing-${rule.sourceStableKey}`}
                  targetTitle={titleFor(rule.sourceStableKey)}
                  description={describeRoutingRule(
                    rule.routingRule,
                    questionTitleByStableKey,
                    optionLabelByStableKey,
                  )}
                  effectLabel={t("questionnaire.skipReview.routing")}
                />
              ))}
            </CollapsibleReportSection>
          ) : null}

          <CollapsibleReportSection
            title={t("questionnaire.skipReview.notAdded")}
            count={notAdded.length}
          >
            {notAdded.map((item, index) => (
              <RuleRow
                key={`not-added-${index}`}
                id={`not-added-${item.kind}-${item.stableKey}`}
                targetTitle={titleFor(item.stableKey)}
                description={describeNotAdded(item)}
                effectLabel={
                  item.kind === "routing"
                    ? t("questionnaire.skipReview.routing")
                    : t("questionnaire.skipReview.shownOnlyIf")
                }
                note={formatIssueCopy({ code: item.code, message: item.message }).text}
              />
            ))}
          </CollapsibleReportSection>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="skip-logic-review-cancel"
          >
            {t("questionnaire.cancel")}
          </Button>
          <Button
            type="button"
            variant="cta"
            onClick={handleApply}
            disabled={approvedCount === 0 || isApplying}
            data-testid="skip-logic-review-apply"
          >
            {approvedCount > 0
              ? t("questionnaire.skipReview.applyCount", { count: approvedCount })
              : t("questionnaire.skipReview.apply")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
